import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ANNONCE_URGENCY, ANNONCE_CATEGORIES } from "@/lib/constants";

export interface Annonce {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  category: string;
  photos: string[];
  photos_jsonb: { url: string; width?: number; height?: number }[] | null;
  wilaya: string;
  commune: string;
  address_hint: string | null;
  budget_min: number | null;
  budget_max: number | null;
  urgency: string;
  status: string;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  // Legacy fields kept for backward compat
  metier: string;
  type_travaux: string;
  delai: string;
  budget: string | null;
}

export const getUrgencyLabel = (u: string, locale = "fr") => {
  const entry = ANNONCE_URGENCY.find((x) => x.value === u);
  if (!entry) return u;
  return locale === "ar" ? entry.label_ar : entry.label;
};

export const getCategoryLabel = (c: string, locale = "fr") => {
  const entry = ANNONCE_CATEGORIES.find((x) => x.value === c);
  if (!entry) return c;
  return locale === "ar" ? entry.label_ar : entry.label;
};

export function useAnnonces(filters?: { wilaya?: string; metier?: string; category?: string }) {
  return useQuery({
    queryKey: ["annonces", filters],
    queryFn: async () => {
      let query = supabase
        .from("annonces")
        .select("*")
        .eq("status", "published")
        .order("created_at", { ascending: false });

      if (filters?.wilaya && filters.wilaya !== "toutes") {
        query = query.eq("wilaya", filters.wilaya);
      }
      if (filters?.category && filters.category !== "tous") {
        // Couvre les annonces récentes (category = slug) ET les annonces legacy (metier = label)
        const legacyMetierMap: Record<string, string> = {
          plumbing:    "Plomberie",
          electricity: "Électricité",
          locksmith:   "Serrurerie",
          masonry:     "Maçonnerie",
          painting:    "Peinture",
          carpentry:   "Menuiserie",
          tiling:      "Carrelage",
          hvac:        "Climatisation",
          insulation:  "Isolation",
        };
        const legacyLabel = legacyMetierMap[filters.category];
        query = legacyLabel
          ? query.or(`category.eq.${filters.category},metier.eq.${legacyLabel}`)
          : query.eq("category", filters.category);
      }
      // Legacy fallback (filtre direct sur metier si explicitement passé)
      if (filters?.metier && filters.metier !== "tous") {
        query = query.eq("metier", filters.metier);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as Annonce[];
    },
  });
}

export function useMyAnnonces(categoryFilter?: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my-annonces", user?.id, categoryFilter],
    queryFn: async () => {
      if (!user) return [];
      let query = supabase
        .from("annonces")
        .select("*")
        .eq("user_id", user.id)
        .in("status", ["published", "closed", "paused"])
        .order("created_at", { ascending: false });

      if (categoryFilter && categoryFilter !== "all") {
        query = query.eq("category", categoryFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as Annonce[];
    },
    enabled: !!user,
  });
}

export function useAnnonceById(id: string | undefined) {
  return useQuery({
    queryKey: ["annonce", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("annonces")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return data as Annonce | null;
    },
    enabled: !!id,
  });
}

export function useUpdateAnnonce() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      title,
      category,
      urgency,
      wilaya,
      commune,
      address_hint,
      description,
      budget_min,
      budget_max,
      photos,
    }: {
      id: string;
      title: string;
      category: string;
      urgency: string;
      wilaya: string;
      commune: string;
      address_hint: string | null;
      description: string | null;
      budget_min: number | null;
      budget_max: number | null;
      photos: string[];
    }) => {
      const { error } = await supabase
        .from("annonces")
        .update({
          title,
          category,
          urgency,
          delai: urgency,
          wilaya,
          commune,
          address_hint,
          description,
          budget_min,
          budget_max,
          budget: budget_min && budget_max ? `${budget_min} - ${budget_max} DA` : budget_min ? `${budget_min} DA` : null,
          photos,
          updated_at: new Date().toISOString(),
        } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["annonce", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["my-annonces"] });
    },
  });
}

export function useDeleteAnnonce() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("annonces").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["annonces"] });
      queryClient.invalidateQueries({ queryKey: ["my-annonces"] });
    },
  });
}

export function useUpdateAnnonceStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const updateData: any = { status };
      if (status === "published") {
        updateData.published_at = new Date().toISOString();
      }
      const { error } = await supabase
        .from("annonces")
        .update(updateData)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["annonces"] });
      queryClient.invalidateQueries({ queryKey: ["my-annonces"] });
    },
  });
}

export function useFavorites() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["favorites", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("favorites")
        .select("*, annonces(*)")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });
}

export function useToggleFavorite() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (annonceId: string) => {
      if (!user) throw new Error("Not authenticated");
      const { data: existing } = await supabase
        .from("favorites")
        .select("id")
        .eq("user_id", user.id)
        .eq("annonce_id", annonceId)
        .maybeSingle();

      if (existing) {
        await supabase.from("favorites").delete().eq("id", existing.id);
        return { added: false };
      } else {
        await supabase.from("favorites").insert({ user_id: user.id, annonce_id: annonceId });
        return { added: true };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favorites"] });
    },
  });
}

export function useIsFavorited(annonceId: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["is-favorited", user?.id, annonceId],
    queryFn: async () => {
      if (!user) return false;
      const { data } = await supabase
        .from("favorites")
        .select("id")
        .eq("user_id", user.id)
        .eq("annonce_id", annonceId)
        .maybeSingle();
      return !!data;
    },
    enabled: !!user,
  });
}
